import { createPollingInterval } from "@sg-protect/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MethodResponse } from "openapi-fetch";
import { useEffect, useRef } from "react";
import { type client, queryOptions } from "../client.ts";
import { releasedCrawlerNames } from "../initialCrawl.ts";
import { hasODataEndpoint } from "../queryableEntitySet.ts";
import { type CrawlState, type CrawlerNames } from "../types.ts";
import { odataMetadataQueryOptions } from "./odata/metadata.ts";

export type CrawlingStatusesResponse = MethodResponse<typeof client, "get", "/bff/getCrawlingStatusesV2">;

export function crawlingStatusesQueryOptions() {
    return queryOptions("get", "/bff/getCrawlingStatusesV2", {});
}

function selectIsPermissionsMatrixReportCrawling(data: CrawlingStatusesResponse): boolean {
    const { siteSettings, permissions } = data;

    return siteSettings?.crawlState === "initializing" || permissions?.crawlState === "initializing";
}

export function isPermissionsMatrixReportCrawlingQueryOptions() {
    return queryOptions("get", "/bff/getCrawlingStatusesV2", {}, { select: selectIsPermissionsMatrixReportCrawling });
}

function isEntityCrawlingComplete(crawlState: CrawlState | null | undefined): boolean {
    return crawlState != null && crawlState !== "initializing";
}

function isCrawlingFinished(data: CrawlingStatusesResponse | undefined): boolean {
    return (
        data != null &&
        releasedCrawlerNames.every(crawler => !data[crawler] || isEntityCrawlingComplete(data[crawler]?.crawlState))
    );
}

const crawlingStatusesRefetchInterval = createPollingInterval<CrawlingStatusesResponse>({
    isPending: data => !isCrawlingFinished(data),
    timeout: Infinity
});

export function usePollCrawlingStatusesQuery(enabled: boolean = true) {
    const queryClient = useQueryClient();
    const { data: metadata } = useQuery(odataMetadataQueryOptions());

    const query = useQuery({
        ...crawlingStatusesQueryOptions(),
        enabled,
        refetchInterval: crawlingStatusesRefetchInterval
    });

    const prevStatesRef = useRef<Partial<Record<CrawlerNames, CrawlState>>>({});

    useEffect(() => {
        if (!query.data) {
            return;
        }

        const newlyUpToDate: CrawlerNames[] = [];

        for (const crawler of releasedCrawlerNames) {
            const current = query.data[crawler]?.crawlState;
            const prev = prevStatesRef.current[crawler];

            if (current === "upToDate" && prev !== "upToDate") {
                newlyUpToDate.push(crawler);
            }
        }

        if (newlyUpToDate.length > 0) {
            const entitySetsToInvalidate = newlyUpToDate.flatMap(
                crawler =>
                    metadata?.entitySets
                        ?.filter(es => es.crawledBy.includes(crawler))
                        .map(es => es.name)
                        .filter(hasODataEndpoint) ?? []
            );

            void Promise.all([
                queryClient.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/governance/overview/getTenantOverview").queryKey
                }),
                ...entitySetsToInvalidate.map(entitySet =>
                    queryClient.invalidateQueries({
                        queryKey: queryOptions("get", `/bff/odata/${entitySet}`).queryKey
                    })
                )
            ]);
        }

        for (const crawler of releasedCrawlerNames) {
            prevStatesRef.current[crawler] = query.data[crawler]?.crawlState;
        }
    }, [query.data, metadata, queryClient]);

    return query;
}
